import "./main.css";

import { BrowserAudioBridge, type BrowserAudioDiagnostics } from "./audio.js";
import type { Am8Key, StorageUnit } from "./emulator.js";
import { AM8_KEY, Emulator, ROM_KIND } from "./emulator.js";
import { createRenderer } from "./renderer/index.js";
import type { RendererDebugState } from "./renderer/types.js";
import { Runloop, type RunloopMetrics } from "./runloop.js";
import { deletePersistedDisk, loadPersistedDisk, savePersistedDisk } from "./storage/indexeddb.js";
import { setupWorkbench } from "./workbench.js";

type DesktopWebSnapshot = {
  booted: boolean;
  cycleAccumulator: number;
  cycleCount: string;
  frameBacklog: number;
  frameNo: number;
  halted: boolean;
  inputCount: number;
  lastError: string | null;
  presentCount: number;
  presentedFrameNo: number;
  rendererBackend: RendererDebugState["backend"] | null;
  rendererFallbackReason: string | null;
  running: boolean;
  screenFocused: boolean;
  skippedFrameCount: number;
  statusText: string;
};

type DesktopWebDebug = {
  copyMemory(address: number, length: number): number[];
  focusScreen(): void;
  getSnapshot(): DesktopWebSnapshot;
  pause(): void;
  queueInput(text: string): void;
  reset(): void;
  resume(): void;
  writeMemory(address: number, data: number[]): void;
  setDiskDirtyForTest(unit: StorageUnit, dirty: boolean): void;
};

type DesktopWebMetrics = RunloopMetrics;

type WorkspaceTabId = "emulator" | "workbench";

type DiskEjectChoice = "cancel" | "download" | "eject";

type DiskUiState = {
  busy: boolean;
  button: HTMLButtonElement;
  dirty: boolean;
  dirtyOverride: boolean | null;
  fileInput: HTMLInputElement;
  fileName: string | null;
  mounted: boolean;
  persistedImage: Uint8Array | null;
  persistedWriteCounter: number;
  bundledButtons: HTMLButtonElement[];
  unit: StorageUnit;
};

type BundledDiskControl = {
  button: HTMLButtonElement;
  disk: DiskUiState;
  fileName: string;
  url: string;
};

type BundledDiskMenuState = {
  panel: HTMLDivElement;
  trigger: HTMLButtonElement;
};

const DISK_PERSIST_INTERVAL_MS = 1000;
const SCREEN_KEYDOWN_CYCLE_BURST = 20_000;
const SCREEN_KEYDOWN_NO_BURST = new Set([
  "AltLeft",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ControlLeft",
  "ControlRight",
  "F1",
  "F3",
  "F5",
  "F7",
  "Home",
  "MetaLeft",
  "ShiftLeft",
  "ShiftRight"
]);

declare global {
  interface Window {
    __am8DesktopWeb?: DesktopWebDebug;
  }
}

const DEFAULT_ROMS = [
  { kind: ROM_KIND.system, url: "./assets/system.rom", label: "system ROM" },
  { kind: ROM_KIND.monitor, url: "./assets/lang.rom", label: "monitor ROM" },
  { kind: ROM_KIND.char, url: "./assets/characters.901225-01.bin", label: "character ROM" }
];

function diskLabel(unit: StorageUnit): string {
  return `Disk ${unit}`;
}

function normalizeDiskDownloadName(unit: StorageUnit, fileName: string | null): string {
  const fallback = `disk${unit}.am8dsk`;
  if (fileName === null || fileName.trim() === "") {
    return fallback;
  }

  return fileName.toLowerCase().endsWith(".am8dsk") ? fileName : `${fileName}.am8dsk`;
}

function triggerDiskDownload(data: Uint8Array, fileName: string): void {
  const blobData = new Uint8Array(data.byteLength);
  blobData.set(data);
  const blob = new Blob([blobData.buffer], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.hidden = true;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

function cloneBytes(data: Uint8Array): Uint8Array {
  const copy = new Uint8Array(data.byteLength);
  copy.set(data);
  return copy;
}

function bytesEqual(left: Uint8Array | null, right: Uint8Array | null): boolean {
  if (left === right) {
    return true;
  }
  if (left === null || right === null || left.byteLength !== right.byteLength) {
    return false;
  }
  for (let i = 0; i < left.byteLength; i++) {
    if (left[i] !== right[i]) {
      return false;
    }
  }
  return true;
}

function expectElement<T extends HTMLElement>(id: string, ctor: { new (): T }): T {
  const element = document.getElementById(id);
  if (!(element instanceof ctor)) {
    throw new Error(`missing required element #${id}`);
  }
  return element;
}

function setWorkspaceTabState(
  activeTab: WorkspaceTabId,
  tabs: Array<{ id: WorkspaceTabId; button: HTMLButtonElement; panel: HTMLElement }>
): void {
  for (const tab of tabs) {
    const active = tab.id === activeTab;
    tab.button.setAttribute("aria-selected", active ? "true" : "false");
    tab.button.tabIndex = active ? 0 : -1;
    tab.button.classList.toggle("is-active", active);
    tab.panel.hidden = !active;
    tab.panel.classList.toggle("is-active", active);
  }
}

const KEYBOARD_CODE_TO_AM8_KEY: Record<string, Am8Key> = {
  AltLeft: AM8_KEY.alt,
  ArrowDown: AM8_KEY.down,
  ArrowLeft: AM8_KEY.left,
  ArrowRight: AM8_KEY.right,
  ArrowUp: AM8_KEY.up,
  Backslash: AM8_KEY.hashApostrophe,
  Backspace: AM8_KEY.backspace,
  BracketLeft: AM8_KEY.ue,
  BracketRight: AM8_KEY.plusAsterisk,
  Comma: AM8_KEY.commaSemicolon,
  ControlLeft: AM8_KEY.ctrl,
  ControlRight: AM8_KEY.ctrl,
  Digit0: AM8_KEY.digit0,
  Digit1: AM8_KEY.digit1,
  Digit2: AM8_KEY.digit2,
  Digit3: AM8_KEY.digit3,
  Digit4: AM8_KEY.digit4,
  Digit5: AM8_KEY.digit5,
  Digit6: AM8_KEY.digit6,
  Digit7: AM8_KEY.digit7,
  Digit8: AM8_KEY.digit8,
  Digit9: AM8_KEY.digit9,
  Enter: AM8_KEY.enter,
  Equal: AM8_KEY.acuteGrave,
  Escape: AM8_KEY.runstop,
  F1: AM8_KEY.f1,
  F3: AM8_KEY.f3,
  F5: AM8_KEY.f5,
  F7: AM8_KEY.f7,
  Home: AM8_KEY.homeClr,
  IntlBackslash: AM8_KEY.ltGtPipe,
  KeyA: AM8_KEY.a,
  KeyB: AM8_KEY.b,
  KeyC: AM8_KEY.c,
  KeyD: AM8_KEY.d,
  KeyE: AM8_KEY.e,
  KeyF: AM8_KEY.f,
  KeyG: AM8_KEY.g,
  KeyH: AM8_KEY.h,
  KeyI: AM8_KEY.i,
  KeyJ: AM8_KEY.j,
  KeyK: AM8_KEY.k,
  KeyL: AM8_KEY.l,
  KeyM: AM8_KEY.m,
  KeyN: AM8_KEY.n,
  KeyO: AM8_KEY.o,
  KeyP: AM8_KEY.p,
  KeyQ: AM8_KEY.q,
  KeyR: AM8_KEY.r,
  KeyS: AM8_KEY.s,
  KeyT: AM8_KEY.t,
  KeyU: AM8_KEY.u,
  KeyV: AM8_KEY.v,
  KeyW: AM8_KEY.w,
  KeyX: AM8_KEY.x,
  KeyY: AM8_KEY.z,
  KeyZ: AM8_KEY.y,
  MetaLeft: AM8_KEY.alt,
  Minus: AM8_KEY.ssQuestion,
  NumpadEnter: AM8_KEY.enter,
  Period: AM8_KEY.periodColon,
  Quote: AM8_KEY.ae,
  Semicolon: AM8_KEY.oe,
  ShiftLeft: AM8_KEY.shiftLeft,
  ShiftRight: AM8_KEY.shiftRight,
  Slash: AM8_KEY.minusUnderscore,
  Space: AM8_KEY.space
};

async function bootstrap(): Promise<void> {
  const canvas = expectElement("screen", HTMLCanvasElement);
  const disk0Toggle = expectElement("disk0-toggle", HTMLButtonElement);
  const disk1Toggle = expectElement("disk1-toggle", HTMLButtonElement);
  const diskLibrary = expectElement("disk-library", HTMLDetailsElement);
  const disk0File = expectElement("disk0-file", HTMLInputElement);
  const disk1File = expectElement("disk1-file", HTMLInputElement);
  const bundledDemoTrigger = expectElement("bundled-demo-trigger", HTMLButtonElement);
  const bundledDemoChoices = expectElement("bundled-demo-choices", HTMLDivElement);
  const bundledDemoDisk0 = expectElement("bundled-demo-disk0", HTMLButtonElement);
  const bundledDemoDisk1 = expectElement("bundled-demo-disk1", HTMLButtonElement);
  const bundledLangTrigger = expectElement("bundled-lang-trigger", HTMLButtonElement);
  const bundledLangChoices = expectElement("bundled-lang-choices", HTMLDivElement);
  const bundledLangDisk0 = expectElement("bundled-lang-disk0", HTMLButtonElement);
  const bundledLangDisk1 = expectElement("bundled-lang-disk1", HTMLButtonElement);
  const diskStatus = expectElement("disk-status", HTMLParagraphElement);
  const diskEjectDialog = expectElement("disk-eject-dialog", HTMLDialogElement);
  const diskEjectMessage = expectElement("disk-eject-message", HTMLParagraphElement);
  const screenFrame = expectElement("screen-frame", HTMLDivElement);
  const screenHint = expectElement("screen-hint", HTMLParagraphElement);
  const runtimeStats = expectElement("runtime-stats", HTMLParagraphElement);
  const activityStatus = expectElement("activity-status", HTMLParagraphElement);
  const audioDebug = expectElement("audio-debug", HTMLParagraphElement);
  const renderDebug = expectElement("render-debug", HTMLParagraphElement);
  const mobileKeyboardOpen = expectElement("mobile-keyboard-open", HTMLButtonElement);
  const mobileKeyboardProxy = expectElement("mobile-keyboard-proxy", HTMLTextAreaElement);
  const status = expectElement("status", HTMLParagraphElement);
  const toggleRun = expectElement("toggle-run", HTMLButtonElement);
  const emulatorStep = expectElement("emulator-step", HTMLButtonElement);
  const emulatorStep10 = expectElement("emulator-step-10", HTMLButtonElement);
  const reset = expectElement("reset", HTMLButtonElement);
  const workspacePanelEmulator = expectElement("workspace-panel-emulator", HTMLElement);
  const workspacePanelWorkbench = expectElement("workspace-panel-workbench", HTMLElement);
  const workspaceTabEmulator = expectElement("workspace-tab-emulator", HTMLButtonElement);
  const workspaceTabWorkbench = expectElement("workspace-tab-workbench", HTMLButtonElement);
  const assemblerSource = expectElement("assembler-source", HTMLTextAreaElement);
  const assemblerStatus = expectElement("assembler-status", HTMLParagraphElement);
  const assembleSource = expectElement("assemble-source", HTMLButtonElement);
  const assembledOutput = expectElement("assembled-output", HTMLPreElement);
  const writeAssembledMemory = expectElement("write-assembled-memory", HTMLButtonElement);
  const disassembledOutput = expectElement("disassembled-output", HTMLPreElement);
  const disassemblyStatus = expectElement("disassembly-status", HTMLParagraphElement);
  const disassembleMemory = expectElement("disassemble-memory", HTMLButtonElement);
  const debuggerPause = expectElement("debugger-pause", HTMLButtonElement);
  const debuggerContinue = expectElement("debugger-continue", HTMLButtonElement);
  const debuggerStep = expectElement("debugger-step", HTMLButtonElement);
  const debuggerStep10 = expectElement("debugger-step-10", HTMLButtonElement);
  const debuggerRefresh = expectElement("debugger-refresh", HTMLButtonElement);
  const debuggerStopReason = expectElement("debugger-stop-reason", HTMLParagraphElement);
  const debuggerRuntimeState = expectElement("debugger-runtime-state", HTMLParagraphElement);
  const machineTabCpu = expectElement("machine-tab-cpu", HTMLButtonElement);
  const machineTabIoc = expectElement("machine-tab-ioc", HTMLButtonElement);
  const machineTabVoc = expectElement("machine-tab-voc", HTMLButtonElement);
  const machineTabAoc = expectElement("machine-tab-aoc", HTMLButtonElement);
  const machineTabTic = expectElement("machine-tab-tic", HTMLButtonElement);
  const machineTabDic = expectElement("machine-tab-dic", HTMLButtonElement);
  const machinePanelCpu = expectElement("machine-panel-cpu", HTMLElement);
  const machinePanelIoc = expectElement("machine-panel-ioc", HTMLElement);
  const machinePanelVoc = expectElement("machine-panel-voc", HTMLElement);
  const machinePanelAoc = expectElement("machine-panel-aoc", HTMLElement);
  const machinePanelTic = expectElement("machine-panel-tic", HTMLElement);
  const machinePanelDic = expectElement("machine-panel-dic", HTMLElement);
  const machineIocClear = expectElement("machine-ioc-clear", HTMLButtonElement);
  const debuggerRegPc = expectElement("debugger-reg-pc", HTMLElement);
  const debuggerRegA = expectElement("debugger-reg-a", HTMLElement);
  const debuggerRegX = expectElement("debugger-reg-x", HTMLElement);
  const debuggerRegY = expectElement("debugger-reg-y", HTMLElement);
  const debuggerRegSp = expectElement("debugger-reg-sp", HTMLElement);
  const debuggerRegP = expectElement("debugger-reg-p", HTMLElement);
  const debuggerRegIrq = expectElement("debugger-reg-irq", HTMLElement);
  const debuggerRegNmi = expectElement("debugger-reg-nmi", HTMLElement);
  const debuggerPhase = expectElement("debugger-phase", HTMLElement);
  const debuggerFlagN = expectElement("debugger-flag-n", HTMLElement);
  const debuggerFlagV = expectElement("debugger-flag-v", HTMLElement);
  const debuggerFlagI = expectElement("debugger-flag-i", HTMLElement);
  const debuggerFlagZ = expectElement("debugger-flag-z", HTMLElement);
  const debuggerFlagC = expectElement("debugger-flag-c", HTMLElement);
  const debuggerFlagFetch = expectElement("debugger-flag-fetch", HTMLElement);
  const debuggerFlagHalt = expectElement("debugger-flag-halt", HTMLElement);
  const debuggerBreakpointAddress = expectElement("debugger-breakpoint-address", HTMLInputElement);
  const debuggerRuleModeBreak = expectElement("debugger-rule-mode-break", HTMLInputElement);
  const debuggerRuleModeTrace = expectElement("debugger-rule-mode-trace", HTMLInputElement);
  const debuggerRuleModeTraceStop = expectElement("debugger-rule-mode-trace-stop", HTMLInputElement);
  const debuggerTraceFieldsRow = expectElement("debugger-trace-fields-row", HTMLElement);
  const debuggerTraceFields = expectElement("debugger-trace-fields", HTMLInputElement);
  const debuggerTraceLimitsRow = expectElement("debugger-trace-limits-row", HTMLElement);
  const debuggerTraceEveryNth = expectElement("debugger-trace-every", HTMLInputElement);
  const debuggerTraceMaxEvents = expectElement("debugger-trace-max", HTMLInputElement);
  const debuggerRuleNameRow = expectElement("debugger-rule-name-row", HTMLElement);
  const debuggerRuleName = expectElement("debugger-rule-name", HTMLInputElement);
  const debuggerAddBreakpoint = expectElement("debugger-add-breakpoint", HTMLButtonElement);
  const debuggerShowBreakpoint = expectElement("debugger-show-breakpoint", HTMLButtonElement);
  const debuggerClearBreakpoints = expectElement("debugger-clear-breakpoints", HTMLButtonElement);
  const debuggerBreakpointsOutput = expectElement("debugger-breakpoints-output", HTMLElement);
  const debuggerPatchAddress = expectElement("debugger-patch-address", HTMLInputElement);
  const debuggerPatchReadLength = expectElement("debugger-patch-read-length", HTMLInputElement);
  const debuggerPatchModeBytes = expectElement("debugger-patch-mode-bytes", HTMLInputElement);
  const debuggerPatchModeFill = expectElement("debugger-patch-mode-fill", HTMLInputElement);
  const debuggerPatchCurrent = expectElement("debugger-patch-current", HTMLElement);
  const debuggerPatchValueLabel = expectElement("debugger-patch-value-label", HTMLLabelElement);
  const debuggerPatchValue = expectElement("debugger-patch-value", HTMLInputElement);
  const debuggerPatchCountRow = expectElement("debugger-patch-count-row", HTMLElement);
  const debuggerPatchCount = expectElement("debugger-patch-count", HTMLInputElement);
  const debuggerPatchRead = expectElement("debugger-patch-read", HTMLButtonElement);
  const debuggerPatchApply = expectElement("debugger-patch-apply", HTMLButtonElement);
  const debuggerPatchWarning = expectElement("debugger-patch-warning", HTMLParagraphElement);
  const debuggerPatchStatus = expectElement("debugger-patch-status", HTMLParagraphElement);
  const debuggerTraceClear = expectElement("debugger-trace-clear", HTMLButtonElement);
  const debuggerTraceToggle = expectElement("debugger-trace-toggle", HTMLButtonElement);
  const debuggerTraceExport = expectElement("debugger-trace-export", HTMLButtonElement);
  const debuggerTraceAutoscroll = expectElement("debugger-trace-autoscroll", HTMLInputElement);
  const debuggerTraceOutput = expectElement("debugger-trace-output", HTMLElement);
  const debuggerDisassemblyOutput = expectElement("debugger-disassembly-output", HTMLPreElement);
  const debuggerFollowPc = expectElement("debugger-follow-pc", HTMLInputElement);
  const machineIocRegisters = expectElement("machine-ioc-registers", HTMLElement);
  const machineIocInternal = expectElement("machine-ioc-internal", HTMLElement);
  const machineIocDerived = expectElement("machine-ioc-derived", HTMLElement);
  const machineVocRegisters = expectElement("machine-voc-registers", HTMLElement);
  const machineVocInternal = expectElement("machine-voc-internal", HTMLElement);
  const machineVocDerived = expectElement("machine-voc-derived", HTMLElement);
  const machineAocRegisters = expectElement("machine-aoc-registers", HTMLElement);
  const machineAocInternal = expectElement("machine-aoc-internal", HTMLElement);
  const machineAocDerived = expectElement("machine-aoc-derived", HTMLElement);
  const machineTicRegisters = expectElement("machine-tic-registers", HTMLElement);
  const machineTicInternal = expectElement("machine-tic-internal", HTMLElement);
  const machineTicDerived = expectElement("machine-tic-derived", HTMLElement);
  const machineDicRegisters = expectElement("machine-dic-registers", HTMLElement);
  const machineDicInternal = expectElement("machine-dic-internal", HTMLElement);
  const machineDicDerived = expectElement("machine-dic-derived", HTMLElement);
  const memoryAddress = expectElement("memory-address", HTMLInputElement);
  const memoryLength = expectElement("memory-length", HTMLInputElement);
  const memoryStatus = expectElement("memory-status", HTMLParagraphElement);
  const memoryOutput = expectElement("memory-output", HTMLPreElement);
  const readMemory = expectElement("read-memory", HTMLButtonElement);
  const spritesRead = expectElement("sprites-read", HTMLButtonElement);
  const spriteDirtyIndicator = expectElement("sprite-dirty-indicator", HTMLElement);
  const spriteHistoryStatus = expectElement("sprite-history-status", HTMLParagraphElement);
  const spriteWriteHint = expectElement("sprite-write-hint", HTMLParagraphElement);
  const spriteStatus = expectElement("sprite-status", HTMLParagraphElement);
  const spriteImportFile = expectElement("sprite-import-file", HTMLInputElement);
  const spriteImportProjectFile = expectElement("sprite-import-project-file", HTMLInputElement);
  const spriteSlotList = expectElement("sprite-slot-list", HTMLDivElement);
  const spriteSelectedSlot = expectElement("sprite-selected-slot", HTMLElement);
  const spritePointer = expectElement("sprite-pointer", HTMLElement);
  const spriteAddress = expectElement("sprite-address", HTMLElement);
  const spriteMulticolor = expectElement("sprite-multicolor", HTMLInputElement);
  const spriteModeSummary = expectElement("sprite-mode-summary", HTMLParagraphElement);
  const spriteSharedHint = expectElement("sprite-shared-hint", HTMLParagraphElement);
  const spriteColorTargets = expectElement("sprite-color-targets", HTMLDivElement);
  const spriteBrushes = expectElement("sprite-brushes", HTMLDivElement);
  const spriteTools = expectElement("sprite-tools", HTMLDivElement);
  const spriteActions = expectElement("sprite-actions", HTMLDivElement);
  const spriteSlotActions = expectElement("sprite-slot-actions", HTMLDivElement);
  const spritePalette = expectElement("sprite-palette", HTMLDivElement);
  const spriteEditorCanvas = expectElement("sprite-editor-canvas", HTMLCanvasElement);
  const spriteNewBlank = expectElement("sprite-new-blank", HTMLButtonElement);
  const spriteUndo = expectElement("sprite-undo", HTMLButtonElement);
  const spriteRedo = expectElement("sprite-redo", HTMLButtonElement);
  const spriteImportSprite = expectElement("sprite-import-sprite", HTMLButtonElement);
  const spriteExportSprite = expectElement("sprite-export-sprite", HTMLButtonElement);
  const spriteImportProject = expectElement("sprite-import-project", HTMLButtonElement);
  const spriteExportProject = expectElement("sprite-export-project", HTMLButtonElement);
  const spriteWrite = expectElement("sprite-write", HTMLButtonElement);
  const spriteByteOutput = expectElement("sprite-byte-output", HTMLTextAreaElement);
  const charactersReadRom = expectElement("characters-read-rom", HTMLButtonElement);
  const charactersReadActive = expectElement("characters-read-active", HTMLButtonElement);
  const charactersImport = expectElement("characters-import", HTMLButtonElement);
  const charactersExport = expectElement("characters-export", HTMLButtonElement);
  const charactersNewSize = expectElement("characters-new-size", HTMLSelectElement);
  const charactersNewBlank = expectElement("characters-new-blank", HTMLButtonElement);
  const charactersBlock = expectElement("characters-block", HTMLSelectElement);
  const charactersJumpInput = expectElement("characters-jump-input", HTMLInputElement);
  const charactersJump = expectElement("characters-jump", HTMLButtonElement);
  const charactersDirtyIndicator = expectElement("characters-dirty-indicator", HTMLElement);
  const charactersImportFile = expectElement("characters-import-file", HTMLInputElement);
  const charactersStatus = expectElement("characters-status", HTMLParagraphElement);
  const charactersAtlasCanvas = expectElement("characters-atlas-canvas", HTMLCanvasElement);
  const charactersSlotCopy = expectElement("characters-slot-copy", HTMLButtonElement);
  const charactersSlotMove = expectElement("characters-slot-move", HTMLButtonElement);
  const charactersSlotSwap = expectElement("characters-slot-swap", HTMLButtonElement);
  const charactersSlotUndo = expectElement("characters-slot-undo", HTMLButtonElement);
  const charactersSlotCancel = expectElement("characters-slot-cancel", HTMLButtonElement);
  const charactersSlotActionSummary = expectElement("characters-slot-action-summary", HTMLParagraphElement);
  const charactersSelectedIndex = expectElement("characters-selected-index", HTMLElement);
  const charactersSelectedPosition = expectElement("characters-selected-position", HTMLElement);
  const charactersEditorCanvas = expectElement("characters-editor-canvas", HTMLCanvasElement);
  const charactersClear = expectElement("characters-clear", HTMLButtonElement);
  const charactersInvert = expectElement("characters-invert", HTMLButtonElement);
  const charactersShiftLeft = expectElement("characters-shift-left", HTMLButtonElement);
  const charactersShiftRight = expectElement("characters-shift-right", HTMLButtonElement);
  const charactersShiftUp = expectElement("characters-shift-up", HTMLButtonElement);
  const charactersShiftDown = expectElement("characters-shift-down", HTMLButtonElement);
  const charactersSourceMeta = expectElement("characters-source-meta", HTMLElement);
  const charactersSizeMeta = expectElement("characters-size-meta", HTMLElement);
  const charactersBlockMeta = expectElement("characters-block-meta", HTMLElement);
  const charactersSourceSummary = expectElement("characters-source-summary", HTMLParagraphElement);
  const charactersImportStartBlock = expectElement("characters-import-start-block", HTMLSelectElement);
  const charactersWriteGlyph = expectElement("characters-write-glyph", HTMLButtonElement);
  const charactersWriteBlock = expectElement("characters-write-block", HTMLButtonElement);
  const charactersWriteDraft = expectElement("characters-write-draft", HTMLButtonElement);
  const charactersWriteHint = expectElement("characters-write-hint", HTMLParagraphElement);
  const charactersByteOutput = expectElement("characters-byte-output", HTMLTextAreaElement);
  const tileMapReadActive = expectElement("tile-map-read-active", HTMLButtonElement);
  const tileMapNewBlank = expectElement("tile-map-new-blank", HTMLButtonElement);
  const tileMapUseCharacterDraft = expectElement("tile-map-use-character-draft", HTMLButtonElement);
  const tileMapEditInCharacters = expectElement("tile-map-edit-in-characters", HTMLButtonElement);
  const tileMapUndo = expectElement("tile-map-undo", HTMLButtonElement);
  const tileMapRedo = expectElement("tile-map-redo", HTMLButtonElement);
  const tileMapImportProject = expectElement("tile-map-import-project", HTMLButtonElement);
  const tileMapImportProjectFile = expectElement("tile-map-import-project-file", HTMLInputElement);
  const tileMapExportProject = expectElement("tile-map-export-project", HTMLButtonElement);
  const tileMapExportByte = expectElement("tile-map-export-byte", HTMLButtonElement);
  const tileMapWriteScreen = expectElement("tile-map-write-screen", HTMLButtonElement);
  const tileMapWriteColor = expectElement("tile-map-write-color", HTMLButtonElement);
  const tileMapWriteAll = expectElement("tile-map-write-all", HTMLButtonElement);
  const tileMapLeftRail = expectElement("tile-map-left-rail", HTMLButtonElement);
  const tileMapRightRail = expectElement("tile-map-right-rail", HTMLButtonElement);
  const tileMapLayout = expectElement("tile-map-layout", HTMLDivElement);
  const tileMapZoom = expectElement("tile-map-zoom", HTMLSelectElement);
  const tileMapBrushMode = expectElement("tile-map-brush-mode", HTMLSelectElement);
  const tileMapBlock = expectElement("tile-map-block", HTMLSelectElement);
  const tileMapReverse = expectElement("tile-map-reverse", HTMLInputElement);
  const tileMapSaveStyle = expectElement("tile-map-save-style", HTMLButtonElement);
  const tileMapCreateStyleFromCell = expectElement("tile-map-create-style-from-cell", HTMLButtonElement);
  const tileMapSuggestStyles = expectElement("tile-map-suggest-styles", HTMLButtonElement);
  const tileMapApplyStyleSelection = expectElement("tile-map-apply-style-selection", HTMLButtonElement);
  const tileMapApplyStyleMatching = expectElement("tile-map-apply-style-matching", HTMLButtonElement);
  const tileMapDirtyIndicator = expectElement("tile-map-dirty-indicator", HTMLElement);
  const tileMapHistoryStatus = expectElement("tile-map-history-status", HTMLParagraphElement);
  const tileMapStatus = expectElement("tile-map-status", HTMLParagraphElement);
  const tileMapSourceMeta = expectElement("tile-map-source-meta", HTMLElement);
  const tileMapModeMeta = expectElement("tile-map-mode-meta", HTMLElement);
  const tileMapBankMeta = expectElement("tile-map-bank-meta", HTMLElement);
  const tileMapScreenBaseMeta = expectElement("tile-map-screen-base-meta", HTMLElement);
  const tileMapColorBaseMeta = expectElement("tile-map-color-base-meta", HTMLElement);
  const tileMapTilesetSourceMeta = expectElement("tile-map-tileset-source-meta", HTMLElement);
  const tileMapAtlasCanvas = expectElement("tile-map-atlas-canvas", HTMLCanvasElement);
  const tileMapPreviewCanvas = expectElement("tile-map-preview-canvas", HTMLCanvasElement);
  const tileMapToolButtons = expectElement("tile-map-tools", HTMLDivElement);
  const tileMapQuickStyles = expectElement("tile-map-quick-styles", HTMLDivElement);
  const tileMapEditorScroller = expectElement("tile-map-editor-scroller", HTMLDivElement);
  const tileMapEditorCanvas = expectElement("tile-map-editor-canvas", HTMLCanvasElement);
  const tileMapSelectedCell = expectElement("tile-map-selected-cell", HTMLElement);
  const tileMapSelectedScreenCode = expectElement("tile-map-selected-screen-code", HTMLElement);
  const tileMapSelectedTile = expectElement("tile-map-selected-tile", HTMLElement);
  const tileMapSelectedColor = expectElement("tile-map-selected-color", HTMLElement);
  const tileMapFgColor = expectElement("tile-map-fg-color", HTMLSelectElement);
  const tileMapBgColor = expectElement("tile-map-bg-color", HTMLSelectElement);
  const tileMapBorderColor = expectElement("tile-map-border-color", HTMLSelectElement);
  const tileMapWriteHint = expectElement("tile-map-write-hint", HTMLParagraphElement);
  const tileMapStyleSuggestions = expectElement("tile-map-style-suggestions", HTMLDivElement);
  const tileMapStyleList = expectElement("tile-map-style-list", HTMLDivElement);
  const tileMapByteOutput = expectElement("tile-map-byte-output", HTMLTextAreaElement);
  const imagesReadActive = expectElement("images-read-active", HTMLButtonElement);
  const imagesNewBlank = expectElement("images-new-blank", HTMLButtonElement);
  const imagesUndo = expectElement("images-undo", HTMLButtonElement);
  const imagesRedo = expectElement("images-redo", HTMLButtonElement);
  const imagesHistoryStatus = expectElement("images-history-status", HTMLParagraphElement);
  const imagesImportProject = expectElement("images-import-project", HTMLButtonElement);
  const imagesImportProjectFile = expectElement("images-import-project-file", HTMLInputElement);
  const imagesImportRawSet = expectElement("images-import-raw-set", HTMLButtonElement);
  const imagesImportRawFiles = expectElement("images-import-raw-files", HTMLInputElement);
  const imagesConvertTrigger = expectElement("images-convert-source", HTMLButtonElement);
  const imagesConvertFile = expectElement("images-convert-file", HTMLInputElement);
  const imagesConvertDialog = expectElement("images-convert-dialog", HTMLDialogElement);
  const imagesConvertFileMeta = expectElement("images-convert-file-meta", HTMLParagraphElement);
  const imagesConvertSourceCanvas = expectElement("images-convert-source-canvas", HTMLCanvasElement);
  const imagesConvertPreviewCanvas = expectElement("images-convert-preview-canvas", HTMLCanvasElement);
  const imagesConvertStatus = expectElement("images-convert-status", HTMLParagraphElement);
  const imagesConvertCropMeta = expectElement("images-convert-crop-meta", HTMLParagraphElement);
  const imagesConvertPreset = expectElement("images-convert-preset", HTMLSelectElement);
  const imagesConvertMode = expectElement("images-convert-mode", HTMLSelectElement);
  const imagesConvertFit = expectElement("images-convert-fit", HTMLSelectElement);
  const imagesConvertPaletteStrategy = expectElement("images-convert-palette-strategy", HTMLSelectElement);
  const imagesConvertDither = expectElement("images-convert-dither", HTMLSelectElement);
  const imagesConvertMatte = expectElement("images-convert-matte", HTMLInputElement);
  const imagesConvertMatteSwatch = expectElement("images-convert-matte-swatch", HTMLSpanElement);
  const imagesConvertManualControls = expectElement("images-convert-manual-controls", HTMLElement);
  const imagesConvertManualZoom = expectElement("images-convert-manual-zoom", HTMLInputElement);
  const imagesConvertManualZoomValue = expectElement("images-convert-manual-zoom-value", HTMLParagraphElement);
  const imagesConvertPanX = expectElement("images-convert-pan-x", HTMLInputElement);
  const imagesConvertPanXValue = expectElement("images-convert-pan-x-value", HTMLParagraphElement);
  const imagesConvertPanY = expectElement("images-convert-pan-y", HTMLInputElement);
  const imagesConvertPanYValue = expectElement("images-convert-pan-y-value", HTMLParagraphElement);
  const imagesConvertResetCrop = expectElement("images-convert-reset-crop", HTMLButtonElement);
  const imagesConvertCancel = expectElement("images-convert-cancel", HTMLButtonElement);
  const imagesConvertApply = expectElement("images-convert-apply", HTMLButtonElement);
  const imagesExportProject = expectElement("images-export-project", HTMLButtonElement);
  const imagesExportRawSet = expectElement("images-export-raw-set", HTMLButtonElement);
  const imagesExportByte = expectElement("images-export-byte", HTMLButtonElement);
  const imagesDirtyIndicator = expectElement("images-dirty-indicator", HTMLElement);
  const imagesLeftRail = expectElement("images-left-rail", HTMLButtonElement);
  const imagesRightRail = expectElement("images-right-rail", HTMLButtonElement);
  const imagesLayout = expectElement("images-layout", HTMLDivElement);
  const imagesModeSelect = expectElement("images-mode-select", HTMLSelectElement);
  const imagesZoom = expectElement("images-zoom", HTMLSelectElement);
  const imagesShowPixelGrid = expectElement("images-show-pixel-grid", HTMLInputElement);
  const imagesShowCellGrid = expectElement("images-show-cell-grid", HTMLInputElement);
  const imagesStatus = expectElement("images-status", HTMLParagraphElement);
  const imagesSourceMeta = expectElement("images-source-meta", HTMLElement);
  const imagesModeMeta = expectElement("images-mode-meta", HTMLElement);
  const imagesBankMeta = expectElement("images-bank-meta", HTMLElement);
  const imagesBitmapBaseMeta = expectElement("images-bitmap-base-meta", HTMLElement);
  const imagesScreenBaseMeta = expectElement("images-screen-base-meta", HTMLElement);
  const imagesColorBaseMeta = expectElement("images-color-base-meta", HTMLElement);
  const imagesPreviewCanvas = expectElement("images-preview-canvas", HTMLCanvasElement);
  const imagesEmptyState = expectElement("images-empty-state", HTMLDivElement);
  const imagesEmptyNewBlank = expectElement("images-empty-new-blank", HTMLButtonElement);
  const imagesEmptyReadActive = expectElement("images-empty-read-active", HTMLButtonElement);
  const imagesEmptyConvert = expectElement("images-empty-convert", HTMLButtonElement);
  const imagesTools = expectElement("images-tools", HTMLDivElement);
  const imagesBrushes = expectElement("images-brushes", HTMLDivElement);
  const imagesEditorScroller = expectElement("images-editor-scroller", HTMLDivElement);
  const imagesEditorCanvas = expectElement("images-editor-canvas", HTMLCanvasElement);
  const imagesSelectedPixel = expectElement("images-selected-pixel", HTMLElement);
  const imagesSelectedCell = expectElement("images-selected-cell", HTMLElement);
  const imagesScreenByte = expectElement("images-screen-byte", HTMLElement);
  const imagesColorNibble = expectElement("images-color-nibble", HTMLElement);
  const imagesModeAFields = expectElement("images-mode-a-fields", HTMLElement);
  const imagesModeBFields = expectElement("images-mode-b-fields", HTMLElement);
  const imagesModeCFields = expectElement("images-mode-c-fields", HTMLElement);
  const imagesApplyColorsToAll = expectElement("images-apply-colors-to-all", HTMLButtonElement);
  const imagesApplyColorsToAllModeB = expectElement("images-apply-colors-to-all-modeb", HTMLButtonElement);
  const imagesCellFg = expectElement("images-cell-fg", HTMLSelectElement);
  const imagesCellFgSwatch = expectElement("images-cell-fg-swatch", HTMLButtonElement);
  const imagesCellBg = expectElement("images-cell-bg", HTMLSelectElement);
  const imagesCellBgSwatch = expectElement("images-cell-bg-swatch", HTMLButtonElement);
  const imagesBgColor = expectElement("images-bg-color", HTMLSelectElement);
  const imagesBgColorSwatch = expectElement("images-bg-color-swatch", HTMLButtonElement);
  const imagesModeBHi = expectElement("images-modeb-hi", HTMLSelectElement);
  const imagesModeBHiSwatch = expectElement("images-modeb-hi-swatch", HTMLButtonElement);
  const imagesModeBLo = expectElement("images-modeb-lo", HTMLSelectElement);
  const imagesModeBLoSwatch = expectElement("images-modeb-lo-swatch", HTMLButtonElement);
  const imagesModeBRam = expectElement("images-modeb-ram", HTMLSelectElement);
  const imagesModeBRamSwatch = expectElement("images-modeb-ram-swatch", HTMLButtonElement);
  const imagesBorderColor = expectElement("images-border-color", HTMLSelectElement);
  const imagesBorderColorSwatch = expectElement("images-border-color-swatch", HTMLButtonElement);
  const imagesPaletteButtons = expectElement("images-palette-buttons", HTMLDivElement);
  const imagesPaletteSelected = expectElement("images-palette-selected", HTMLElement);
  const imagesColorTargetStatus = expectElement("images-color-target-status", HTMLParagraphElement);
  const imagesExpertHints = expectElement("images-expert-hints", HTMLUListElement);
  const imagesPaletteHex = expectElement("images-palette-hex", HTMLInputElement);
  const imagesPaletteApply = expectElement("images-palette-apply", HTMLButtonElement);
  const imagesWriteBitmap = expectElement("images-write-bitmap", HTMLButtonElement);
  const imagesWriteScreen = expectElement("images-write-screen", HTMLButtonElement);
  const imagesWriteColor = expectElement("images-write-color", HTMLButtonElement);
  const imagesWritePalette = expectElement("images-write-palette", HTMLButtonElement);
  const imagesWriteAll = expectElement("images-write-all", HTMLButtonElement);
  const imagesWriteHint = expectElement("images-write-hint", HTMLParagraphElement);
  const imagesByteOutput = expectElement("images-byte-output", HTMLTextAreaElement);
  const panelAssembler = expectElement("workbench-panel-assembler", HTMLElement);
  const panelDisassembler = expectElement("workbench-panel-disassembler", HTMLElement);
  const panelDebugger = expectElement("workbench-panel-debugger", HTMLElement);
  const panelSprites = expectElement("workbench-panel-sprites", HTMLElement);
  const panelCharacters = expectElement("workbench-panel-characters", HTMLElement);
  const panelTileMap = expectElement("workbench-panel-tile-map", HTMLElement);
  const panelImages = expectElement("workbench-panel-images", HTMLElement);
  const panelTracker = expectElement("workbench-panel-tracker", HTMLElement);
  const panelReference = expectElement("workbench-panel-reference", HTMLElement);
  const panelManuals = expectElement("workbench-panel-manuals", HTMLElement);
  const tabAssembler = expectElement("workbench-tab-assembler", HTMLButtonElement);
  const tabDisassembler = expectElement("workbench-tab-disassembler", HTMLButtonElement);
  const tabDebugger = expectElement("workbench-tab-debugger", HTMLButtonElement);
  const tabSprites = expectElement("workbench-tab-sprites", HTMLButtonElement);
  const tabCharacters = expectElement("workbench-tab-characters", HTMLButtonElement);
  const tabTileMap = expectElement("workbench-tab-tile-map", HTMLButtonElement);
  const tabImages = expectElement("workbench-tab-images", HTMLButtonElement);
  const tabTracker = expectElement("workbench-tab-tracker", HTMLButtonElement);
  const tabReference = expectElement("workbench-tab-reference", HTMLButtonElement);
  const tabManuals = expectElement("workbench-tab-manuals", HTMLButtonElement);

  const setActivityStatus = (message: string): void => {
    activityStatus.textContent = message;
  };
  const renderRunStatus = (runState: "running" | "paused" | "halted"): void => {
    status.dataset.state = runState;
    status.textContent = runState;
  };

  setActivityStatus("Loading Wasm module...");
  renderRunStatus("paused");
  const emulator = await Emulator.create();
  const audioBridge = new BrowserAudioBridge(emulator);
  const renderer = createRenderer(canvas);
  const workbench = setupWorkbench(emulator, {
    assembledOutput,
    assemblerSource,
    assemblerStatus,
    assembleTrigger: assembleSource,
    writeMemoryTrigger: writeAssembledMemory,
    disassembledOutput,
    disassemblyStatus,
    disassembleTrigger: disassembleMemory,
    pauseTrigger: debuggerPause,
    continueTrigger: debuggerContinue,
    stepTrigger: debuggerStep,
    step10Trigger: debuggerStep10,
    refreshTrigger: debuggerRefresh,
    stopReason: debuggerStopReason,
    runtimeState: debuggerRuntimeState,
    machineTabCpu,
    machineTabIoc,
    machineTabVoc,
    machineTabAoc,
    machineTabTic,
    machineTabDic,
    machinePanelCpu,
    machinePanelIoc,
    machinePanelVoc,
    machinePanelAoc,
    machinePanelTic,
    machinePanelDic,
    iocClearTrigger: machineIocClear,
    registerPc: debuggerRegPc,
    registerA: debuggerRegA,
    registerX: debuggerRegX,
    registerY: debuggerRegY,
    registerSp: debuggerRegSp,
    registerP: debuggerRegP,
    registerIrq: debuggerRegIrq,
    registerNmi: debuggerRegNmi,
    phaseValue: debuggerPhase,
    flagN: debuggerFlagN,
    flagV: debuggerFlagV,
    flagI: debuggerFlagI,
    flagZ: debuggerFlagZ,
    flagC: debuggerFlagC,
    flagFetch: debuggerFlagFetch,
    flagHalt: debuggerFlagHalt,
    breakpointAddress: debuggerBreakpointAddress,
    ruleModeBreak: debuggerRuleModeBreak,
    ruleModeTrace: debuggerRuleModeTrace,
    ruleModeTraceStop: debuggerRuleModeTraceStop,
    traceFieldsRow: debuggerTraceFieldsRow,
    traceFields: debuggerTraceFields,
    traceLimitsRow: debuggerTraceLimitsRow,
    traceEveryNth: debuggerTraceEveryNth,
    traceMaxEvents: debuggerTraceMaxEvents,
    ruleNameRow: debuggerRuleNameRow,
    ruleName: debuggerRuleName,
    addBreakpointTrigger: debuggerAddBreakpoint,
    showBreakpointTrigger: debuggerShowBreakpoint,
    clearBreakpointsTrigger: debuggerClearBreakpoints,
    breakpointsOutput: debuggerBreakpointsOutput,
    patchAddress: debuggerPatchAddress,
    patchReadLength: debuggerPatchReadLength,
    patchModeBytes: debuggerPatchModeBytes,
    patchModeFill: debuggerPatchModeFill,
    patchCurrent: debuggerPatchCurrent,
    patchValueLabel: debuggerPatchValueLabel,
    patchValue: debuggerPatchValue,
    patchCountRow: debuggerPatchCountRow,
    patchCount: debuggerPatchCount,
    patchReadTrigger: debuggerPatchRead,
    patchApplyTrigger: debuggerPatchApply,
    patchWarning: debuggerPatchWarning,
    patchStatus: debuggerPatchStatus,
    traceClearTrigger: debuggerTraceClear,
    traceToggleTrigger: debuggerTraceToggle,
    traceExportTrigger: debuggerTraceExport,
    traceAutoscrollToggle: debuggerTraceAutoscroll,
    traceOutput: debuggerTraceOutput,
    disassemblyOutput: debuggerDisassemblyOutput,
    followPcToggle: debuggerFollowPc,
    iocRegisters: machineIocRegisters,
    iocInternal: machineIocInternal,
    iocDerived: machineIocDerived,
    vocRegisters: machineVocRegisters,
    vocInternal: machineVocInternal,
    vocDerived: machineVocDerived,
    aocRegisters: machineAocRegisters,
    aocInternal: machineAocInternal,
    aocDerived: machineAocDerived,
    ticRegisters: machineTicRegisters,
    ticInternal: machineTicInternal,
    ticDerived: machineTicDerived,
    dicRegisters: machineDicRegisters,
    dicInternal: machineDicInternal,
    dicDerived: machineDicDerived,
    memoryAddress,
    memoryLength,
    memoryOutput,
    memoryStatus,
    memoryTrigger: readMemory,
    addressValue: spriteAddress,
    brushes: spriteBrushes,
    byteOutput: spriteByteOutput,
    colorTargets: spriteColorTargets,
    draftIndicator: spriteDirtyIndicator,
    editorCanvas: spriteEditorCanvas,
    exportProjectTrigger: spriteExportProject,
    exportSpriteTrigger: spriteExportSprite,
    historyStatus: spriteHistoryStatus,
    importProjectInput: spriteImportProjectFile,
    importProjectTrigger: spriteImportProject,
    importSpriteInput: spriteImportFile,
    importSpriteTrigger: spriteImportSprite,
    modeSummary: spriteModeSummary,
    multicolorToggle: spriteMulticolor,
    newBlankTrigger: spriteNewBlank,
    palette: spritePalette,
    panel: panelSprites,
    pointerValue: spritePointer,
    readTrigger: spritesRead,
    redoTrigger: spriteRedo,
    selectedSlotValue: spriteSelectedSlot,
    sharedHint: spriteSharedHint,
    slotActionButtons: spriteSlotActions,
    slotList: spriteSlotList,
    spriteActionButtons: spriteActions,
    status: spriteStatus,
    tools: spriteTools,
    undoTrigger: spriteUndo,
    writeSummary: spriteWriteHint,
    writeTrigger: spriteWrite,
    atlasCanvas: charactersAtlasCanvas,
    blockMeta: charactersBlockMeta,
    blockSelect: charactersBlock,
    cancelSlotActionTrigger: charactersSlotCancel,
    clearTrigger: charactersClear,
    charsetStatus: charactersStatus,
    copySlotTrigger: charactersSlotCopy,
    dirtyIndicator: charactersDirtyIndicator,
    createBlankTrigger: charactersNewBlank,
    exportTrigger: charactersExport,
    glyphByteOutput: charactersByteOutput,
    glyphEditorCanvas: charactersEditorCanvas,
    goToInput: charactersJumpInput,
    goToTrigger: charactersJump,
    importFileInput: charactersImportFile,
    importStartBlock: charactersImportStartBlock,
    importTrigger: charactersImport,
    invertTrigger: charactersInvert,
    moveSlotTrigger: charactersSlotMove,
    newBlankSize: charactersNewSize,
    readActiveTrigger: charactersReadActive,
    readRomTrigger: charactersReadRom,
    selectedIndex: charactersSelectedIndex,
    selectedPosition: charactersSelectedPosition,
    shiftDownTrigger: charactersShiftDown,
    shiftLeftTrigger: charactersShiftLeft,
    shiftRightTrigger: charactersShiftRight,
    shiftUpTrigger: charactersShiftUp,
    sizeMeta: charactersSizeMeta,
    sourceMeta: charactersSourceMeta,
    sourceSummary: charactersSourceSummary,
    slotActionSummary: charactersSlotActionSummary,
    swapSlotTrigger: charactersSlotSwap,
    undoSlotActionTrigger: charactersSlotUndo,
    writeBlockTrigger: charactersWriteBlock,
    writeDraftTrigger: charactersWriteDraft,
    writeGlyphTrigger: charactersWriteGlyph,
    writeHint: charactersWriteHint,
    tileMapEditor: {
      applyStyleToMatchingTrigger: tileMapApplyStyleMatching,
      applyStyleToSelectionTrigger: tileMapApplyStyleSelection,
      atlasCanvas: tileMapAtlasCanvas,
      bankMeta: tileMapBankMeta,
      bgColorSelect: tileMapBgColor,
      blockSelect: tileMapBlock,
      borderColorSelect: tileMapBorderColor,
      brushModeSelect: tileMapBrushMode,
      byteOutput: tileMapByteOutput,
      colorBaseMeta: tileMapColorBaseMeta,
      createStyleFromCellTrigger: tileMapCreateStyleFromCell,
      dirtyIndicator: tileMapDirtyIndicator,
      editInCharacterEditorTrigger: tileMapEditInCharacters,
      editorCanvas: tileMapEditorCanvas,
      editorScroller: tileMapEditorScroller,
      exportByteTrigger: tileMapExportByte,
      exportProjectTrigger: tileMapExportProject,
      fgColorSelect: tileMapFgColor,
      historyStatus: tileMapHistoryStatus,
      importProjectInput: tileMapImportProjectFile,
      importProjectTrigger: tileMapImportProject,
      layout: tileMapLayout,
      leftRail: tileMapLeftRail,
      modeMeta: tileMapModeMeta,
      newBlankTrigger: tileMapNewBlank,
      panel: panelTileMap,
      previewCanvas: tileMapPreviewCanvas,
      quickStyles: tileMapQuickStyles,
      readActiveTrigger: tileMapReadActive,
      redoTrigger: tileMapRedo,
      reverseToggle: tileMapReverse,
      rightRail: tileMapRightRail,
      saveStyleTrigger: tileMapSaveStyle,
      screenBaseMeta: tileMapScreenBaseMeta,
      selectedCellMeta: tileMapSelectedCell,
      selectedColorMeta: tileMapSelectedColor,
      selectedScreenCodeMeta: tileMapSelectedScreenCode,
      selectedTileMeta: tileMapSelectedTile,
      sourceMeta: tileMapSourceMeta,
      status: tileMapStatus,
      styleList: tileMapStyleList,
      styleSuggestions: tileMapStyleSuggestions,
      suggestStylesTrigger: tileMapSuggestStyles,
      tilesetSourceMeta: tileMapTilesetSourceMeta,
      toolButtons: tileMapToolButtons,
      undoTrigger: tileMapUndo,
      useCharacterDraftTrigger: tileMapUseCharacterDraft,
      writeAllTrigger: tileMapWriteAll,
      writeColorTrigger: tileMapWriteColor,
      writeHint: tileMapWriteHint,
      writeScreenTrigger: tileMapWriteScreen,
      zoomSelect: tileMapZoom
    },
    imageEditor: {
      applyColorsToAllModeATrigger: imagesApplyColorsToAll,
      applyColorsToAllModeBTrigger: imagesApplyColorsToAllModeB,
      bgColorSelect: imagesBgColor,
      bgColorSwatch: imagesBgColorSwatch,
      bitmapBaseMeta: imagesBitmapBaseMeta,
      bankMeta: imagesBankMeta,
      borderColorSelect: imagesBorderColor,
      borderColorSwatch: imagesBorderColorSwatch,
      brushes: imagesBrushes,
      byteOutput: imagesByteOutput,
      cellBgSelect: imagesCellBg,
      cellBgSwatch: imagesCellBgSwatch,
      cellFgSelect: imagesCellFg,
      cellFgSwatch: imagesCellFgSwatch,
      colorBaseMeta: imagesColorBaseMeta,
      colorTargetStatus: imagesColorTargetStatus,
      colorNibbleValue: imagesColorNibble,
      convertApplyTrigger: imagesConvertApply,
      convertCancelTrigger: imagesConvertCancel,
      convertCropMeta: imagesConvertCropMeta,
      convertDitherSelect: imagesConvertDither,
      convertDialog: imagesConvertDialog,
      convertFileInput: imagesConvertFile,
      convertFileMeta: imagesConvertFileMeta,
      convertFitSelect: imagesConvertFit,
      convertManualControls: imagesConvertManualControls,
      convertManualPanXInput: imagesConvertPanX,
      convertManualPanXValue: imagesConvertPanXValue,
      convertManualPanYInput: imagesConvertPanY,
      convertManualPanYValue: imagesConvertPanYValue,
      convertManualZoomInput: imagesConvertManualZoom,
      convertManualZoomValue: imagesConvertManualZoomValue,
      convertMatteInput: imagesConvertMatte,
      convertMatteSwatch: imagesConvertMatteSwatch,
      convertModeSelect: imagesConvertMode,
      convertPresetSelect: imagesConvertPreset,
      convertPaletteStrategySelect: imagesConvertPaletteStrategy,
      convertPreviewCanvas: imagesConvertPreviewCanvas,
      convertResetCropTrigger: imagesConvertResetCrop,
      convertSourceCanvas: imagesConvertSourceCanvas,
      convertStatus: imagesConvertStatus,
      convertTrigger: imagesConvertTrigger,
      dirtyIndicator: imagesDirtyIndicator,
      emptyConvertTrigger: imagesEmptyConvert,
      emptyNewBlankTrigger: imagesEmptyNewBlank,
      emptyReadActiveTrigger: imagesEmptyReadActive,
      emptyState: imagesEmptyState,
      editorCanvas: imagesEditorCanvas,
      editorScroller: imagesEditorScroller,
      expertHints: imagesExpertHints,
      layout: imagesLayout,
      leftRail: imagesLeftRail,
      exportByteTrigger: imagesExportByte,
      exportProjectTrigger: imagesExportProject,
      exportRawSetTrigger: imagesExportRawSet,
      historyStatus: imagesHistoryStatus,
      importProjectInput: imagesImportProjectFile,
      importProjectTrigger: imagesImportProject,
      importRawSetInput: imagesImportRawFiles,
      importRawSetTrigger: imagesImportRawSet,
      panel: panelImages,
      rightRail: imagesRightRail,
      modeAFields: imagesModeAFields,
      modeBFields: imagesModeBFields,
      modeBHiSelect: imagesModeBHi,
      modeBHiSwatch: imagesModeBHiSwatch,
      modeBLoSelect: imagesModeBLo,
      modeBLoSwatch: imagesModeBLoSwatch,
      modeBRamSelect: imagesModeBRam,
      modeBRamSwatch: imagesModeBRamSwatch,
      modeCFields: imagesModeCFields,
      modeMeta: imagesModeMeta,
      modeSelect: imagesModeSelect,
      newBlankTrigger: imagesNewBlank,
      paletteApplyTrigger: imagesPaletteApply,
      paletteButtons: imagesPaletteButtons,
      paletteHexInput: imagesPaletteHex,
      paletteSelectedMeta: imagesPaletteSelected,
      previewCanvas: imagesPreviewCanvas,
      readActiveTrigger: imagesReadActive,
      redoTrigger: imagesRedo,
      screenBaseMeta: imagesScreenBaseMeta,
      screenByteValue: imagesScreenByte,
      selectedCellMeta: imagesSelectedCell,
      selectedPixelMeta: imagesSelectedPixel,
      showCellGridToggle: imagesShowCellGrid,
      showPixelGridToggle: imagesShowPixelGrid,
      sourceMeta: imagesSourceMeta,
      status: imagesStatus,
      tools: imagesTools,
      undoTrigger: imagesUndo,
      writeAllTrigger: imagesWriteAll,
      writeBitmapTrigger: imagesWriteBitmap,
      writeColorTrigger: imagesWriteColor,
      writeHint: imagesWriteHint,
      writePaletteTrigger: imagesWritePalette,
      writeScreenTrigger: imagesWriteScreen,
      zoomSelect: imagesZoom
    },
    panelAssembler,
    panelDisassembler,
    panelDebugger,
    panelSprites,
    panelCharacters,
    panelTileMap,
    panelImages,
    panelTracker,
    panelReference,
    panelManuals,
    trackerHost: {
      beginAudioDiagnosticsSession() {
        audioBridge.beginDiagnosticsSession();
      },
      endAudioDiagnosticsSession() {
        audioBridge.endDiagnosticsSession();
      },
      getAudioDiagnostics() {
        return formatTrackerAudioDiagnostics(audioBridge.getDiagnostics());
      },
      focusScreen() {
        focusScreen();
      },
      getAudioStatus() {
        return audioDebug.textContent ?? "Audio: idle";
      },
      setActivityStatus(message) {
        setActivityStatus(message);
      },
      startAudio() {
        tryStartAudio();
      }
    },
    tabAssembler,
    tabDisassembler,
    tabDebugger,
    tabSprites,
    tabCharacters,
    tabTileMap,
    tabImages,
    tabTracker,
    tabReference,
    tabManuals
  });
  const workspaceTabs = [
    { id: "emulator" as const, button: workspaceTabEmulator, panel: workspacePanelEmulator },
    { id: "workbench" as const, button: workspaceTabWorkbench, panel: workspacePanelWorkbench }
  ];
  let activeWorkspaceTab: WorkspaceTabId = window.location.hash.startsWith("#reference/") ? "workbench" : "emulator";
  const showWorkspaceTab = (tabId: WorkspaceTabId): void => {
    activeWorkspaceTab = tabId;
    setWorkspaceTabState(tabId, workspaceTabs);
    workbench.setVisible(tabId === "workbench");
    if (tabId === "workbench") {
      workbench.refreshDebugger();
      workbench.refreshCharacters();
      workbench.refreshTileMap();
      workbench.refreshImages();
      workbench.refreshManuals();
      workbench.refreshSprites();
      workbench.refreshTracker();
    }
  };
  const showWorkbenchForReferenceHash = (): void => {
    if (window.location.hash.startsWith("#reference/")) {
      showWorkspaceTab("workbench");
    }
  };
  for (const tab of workspaceTabs) {
    tab.button.addEventListener("click", () => {
      showWorkspaceTab(tab.id);
    });
  }
  showWorkspaceTab(activeWorkspaceTab);
  window.addEventListener("hashchange", showWorkbenchForReferenceHash);
  const diskStates: DiskUiState[] = [
    {
      busy: false,
      button: disk0Toggle,
      bundledButtons: [bundledDemoDisk0, bundledLangDisk0],
      dirty: false,
      dirtyOverride: null,
      fileInput: disk0File,
      fileName: null,
      mounted: false,
      persistedImage: null,
      persistedWriteCounter: 0,
      unit: 0
    },
    {
      busy: false,
      button: disk1Toggle,
      bundledButtons: [bundledDemoDisk1, bundledLangDisk1],
      dirty: false,
      dirtyOverride: null,
      fileInput: disk1File,
      fileName: null,
      mounted: false,
      persistedImage: null,
      persistedWriteCounter: 0,
      unit: 1
    }
  ];
  const bundledDiskControls: BundledDiskControl[] = [
    {
      button: bundledDemoDisk0,
      disk: diskStates[0],
      fileName: "demo.am8dsk",
      url: "./assets/demo.am8dsk"
    },
    {
      button: bundledDemoDisk1,
      disk: diskStates[1],
      fileName: "demo.am8dsk",
      url: "./assets/demo.am8dsk"
    },
    {
      button: bundledLangDisk0,
      disk: diskStates[0],
      fileName: "lang.am8dsk",
      url: "./assets/lang.am8dsk"
    },
    {
      button: bundledLangDisk1,
      disk: diskStates[1],
      fileName: "lang.am8dsk",
      url: "./assets/lang.am8dsk"
    }
  ];
  const bundledDiskMenus: BundledDiskMenuState[] = [
    {
      panel: bundledDemoChoices,
      trigger: bundledDemoTrigger
    },
    {
      panel: bundledLangChoices,
      trigger: bundledLangTrigger
    }
  ];
  const bundledDiskCache = new Map<string, Uint8Array>();
  let diskPersistenceEnabled = true;
  let diskRefreshInFlight = false;
  let acceptedInputCount = 0;
  let lastError: string | null = null;
  let escapeReleaseTimer: number | null = null;
  let mobileKeyboardCarryText = "";
  let mobileKeyboardLineValue = "";
  let mobileKeyboardPendingSpecialInput: "backspace" | "enter" | null = null;
  let mobileKeyboardScrollX = 0;
  let mobileKeyboardScrollY = 0;
  let mobileKeyboardScrollRestoreFrame = 0;
  const activeScreenCodes = new Map<string, Am8Key>();
  const activeScreenKeyCounts = new Map<Am8Key, number>();

  const renderScreenFocus = (focused: boolean, source: "screen" | "mobile" = "screen"): void => {
    screenFrame.classList.toggle("is-focused", focused);
    screenHint.textContent = focused
      ? source === "mobile"
        ? "Keyboard ready. Type directly into the emulator."
        : "Screen focused. Type directly into the emulator."
      : "Click the screen, then type directly into the emulator.";
  };

  let rateSampleCycles = 0n;
  let rateSampleFrames = 0;
  let rateSamplePresents = 0;
  let rateSampleTimestampMs = performance.now();
  let rateIgnoredBurstCycles = 0n;
  let rateIgnoredBurstFrames = 0;
  let lastFramesPerSecond = 0;
  let lastCyclesPerSecond = 0;
  let lastPresentsPerSecond = 0;

  let lastRuntimeStatsStateText = "";
  let lastRuntimeStatsDebugSyncHint = "";
  let lastRuntimeStatsRunning = false;
  let lastRuntimeStatsUpdateMs = 0;

  const adjustedRuntimeRateCounts = (
    currentCycles: bigint,
    currentFrames: number
  ): { cycles: bigint; frames: number } => {
    if (currentCycles < rateIgnoredBurstCycles || currentFrames < rateIgnoredBurstFrames) {
      rateIgnoredBurstCycles = 0n;
      rateIgnoredBurstFrames = 0;
    }
    return {
      cycles: currentCycles - rateIgnoredBurstCycles,
      frames: currentFrames - rateIgnoredBurstFrames
    };
  };

  const noteIgnoredRuntimeBurst = (
    beforeCycles: bigint,
    beforeFrames: number,
    afterCycles: bigint,
    afterFrames: number
  ): void => {
    if (afterCycles <= beforeCycles && afterFrames <= beforeFrames) {
      return;
    }
    if (afterCycles > beforeCycles) {
      rateIgnoredBurstCycles += afterCycles - beforeCycles;
    }
    if (afterFrames > beforeFrames) {
      rateIgnoredBurstFrames += afterFrames - beforeFrames;
    }
  };

  const updateRuntimeRates = (
    snapshot: DesktopWebMetrics
  ): { cyclesPerSecond: number; framesPerSecond: number; presentsPerSecond: number } => {
    const currentCycles = BigInt(snapshot.cycleCount);
    const currentFrames = snapshot.frameNo;
    const currentPresents = snapshot.presentCount;
    const adjusted = adjustedRuntimeRateCounts(currentCycles, currentFrames);
    const now = performance.now();

    if (!snapshot.running) {
      rateSampleCycles = adjusted.cycles;
      rateSampleFrames = adjusted.frames;
      rateSamplePresents = currentPresents;
      rateSampleTimestampMs = now;
      lastFramesPerSecond = 0;
      lastCyclesPerSecond = 0;
      lastPresentsPerSecond = 0;
      return { cyclesPerSecond: 0, framesPerSecond: 0, presentsPerSecond: 0 };
    }

    const elapsedMs = now - rateSampleTimestampMs;
    if (elapsedMs >= 1000) {
      lastCyclesPerSecond = Number(adjusted.cycles - rateSampleCycles) / (elapsedMs / 1000);
      lastFramesPerSecond = (adjusted.frames - rateSampleFrames) / (elapsedMs / 1000);
      lastPresentsPerSecond = (currentPresents - rateSamplePresents) / (elapsedMs / 1000);
      rateSampleCycles = adjusted.cycles;
      rateSampleFrames = adjusted.frames;
      rateSamplePresents = currentPresents;
      rateSampleTimestampMs = now;
    }

    return {
      cyclesPerSecond: lastCyclesPerSecond,
      framesPerSecond: lastFramesPerSecond,
      presentsPerSecond: lastPresentsPerSecond
    };
  };

  const renderStats = (snapshot: DesktopWebMetrics): void => {
    const stateText = snapshot.state === "running" ? "running" : snapshot.state === "halted" ? "halted" : "paused";
    const debugSyncHint = snapshot.debuggerActive && stateText === "paused" ? " | Screen synced" : "";
    const { cyclesPerSecond, framesPerSecond, presentsPerSecond } = updateRuntimeRates(snapshot);
    const now = performance.now();
    renderRunStatus(stateText);
    renderRendererDebug();

    const shouldUpdateRuntimeStats =
      !snapshot.running ||
      !lastRuntimeStatsRunning ||
      lastRuntimeStatsStateText !== stateText ||
      lastRuntimeStatsDebugSyncHint !== debugSyncHint ||
      now - lastRuntimeStatsUpdateMs >= 1000;

    if (!shouldUpdateRuntimeStats) {
      return;
    }

    runtimeStats.textContent =
      `Frames ${snapshot.frameNo} | Frames/s ${framesPerSecond.toFixed(1)} | Cycles ${snapshot.cycleCount} | ` +
      `Cycles/s ${Math.round(cyclesPerSecond)} | Presents/s ${presentsPerSecond.toFixed(1)} | ` +
      `Presented ${snapshot.presentedFrameNo} | Frame Backlog ${snapshot.frameBacklog} | ` +
      `Skipped Frames ${snapshot.skippedFrameCount} | Acc ${snapshot.cycleAccumulator.toFixed(2)} | ` +
      `State ${stateText}${debugSyncHint}`;
    lastRuntimeStatsStateText = stateText;
    lastRuntimeStatsDebugSyncHint = debugSyncHint;
    lastRuntimeStatsRunning = snapshot.running;
    lastRuntimeStatsUpdateMs = now;
  };

  let lastRendererDebugText = "";
  let lastRendererFallback = "";
  const renderRendererDebug = (): void => {
    const debugState = renderer.getDebugState();
    const text =
      debugState.backend === "webgl"
        ? debugState.fallbackReason === null
          ? "Renderer: WebGL"
          : `Renderer: WebGL - ${debugState.fallbackReason}`
        : debugState.fallbackReason === null
          ? "Renderer: Canvas2D"
          : `Renderer: Canvas2D fallback - ${debugState.fallbackReason}`;
    if (text !== lastRendererDebugText) {
      renderDebug.textContent = text;
      lastRendererDebugText = text;
    }

    const fallback = debugState.fallbackReason ?? "";
    if (fallback !== lastRendererFallback) {
      renderDebug.classList.toggle("render-debug--fallback", fallback !== "");
      lastRendererFallback = fallback;
    }
  };

  const renderAudioDebug = (): void => {
    const { mode, reason } = audioBridge.getDebugState();

    audioDebug.classList.remove(
      "audio-debug--fallback",
      "audio-debug--unavailable",
      "audio-debug--worklet"
    );
    if (mode === "audio-worklet") {
      audioDebug.classList.add("audio-debug--worklet");
      audioDebug.textContent = "Audio: AudioWorklet";
      return;
    }
    if (mode === "script-processor") {
      audioDebug.classList.add("audio-debug--fallback");
      audioDebug.textContent =
        reason === null
          ? "Audio: ScriptProcessor fallback"
          : `Audio: ScriptProcessor fallback - ${reason}`;
      return;
    }
    if (mode === "unavailable") {
      audioDebug.classList.add("audio-debug--unavailable");
      audioDebug.textContent =
        reason === null ? "Audio: unavailable" : `Audio: unavailable - ${reason}`;
      return;
    }

    audioDebug.textContent = reason === null ? "Audio: idle" : `Audio: idle - ${reason}`;
  };

  const formatTrackerAudioDiagnostics = (diagnostics: BrowserAudioDiagnostics): string =>
    `Queue ${diagnostics.queueFrames} fr | Peak ${diagnostics.highWaterFrames} fr | ` +
    `Underruns ${diagnostics.underrunCount} | Dropped ${diagnostics.droppedFrames} | ` +
    `Emulator backlog ${diagnostics.emulatorBacklogFrames} fr`;

  const focusScreen = (): void => {
    canvas.focus();
  };

  const focusMobileKeyboard = (): void => {
    mobileKeyboardProxy.focus({ preventScroll: true });
    mobileKeyboardProxy.value = "";
    mobileKeyboardCarryText = "";
    mobileKeyboardLineValue = "";
    mobileKeyboardPendingSpecialInput = null;
    mobileKeyboardScrollX = window.scrollX;
    mobileKeyboardScrollY = window.scrollY;
  };

  const captureMobileKeyboardScroll = (): void => {
    mobileKeyboardScrollX = window.scrollX;
    mobileKeyboardScrollY = window.scrollY;
  };

  const scheduleMobileKeyboardScrollRestore = (): void => {
    if (mobileKeyboardScrollRestoreFrame !== 0) {
      window.cancelAnimationFrame(mobileKeyboardScrollRestoreFrame);
    }
    mobileKeyboardScrollRestoreFrame = window.requestAnimationFrame(() => {
      mobileKeyboardScrollRestoreFrame = 0;
      window.scrollTo(mobileKeyboardScrollX, mobileKeyboardScrollY);
      window.requestAnimationFrame(() => {
        window.scrollTo(mobileKeyboardScrollX, mobileKeyboardScrollY);
      });
    });
  };

  const noteMobileKeyboardText = (text: string): void => {
    if (text === "") {
      return;
    }
    mobileKeyboardLineValue += text;
  };

  const noteMobileKeyboardBackspace = (): void => {
    if (mobileKeyboardLineValue === "") {
      return;
    }
    mobileKeyboardLineValue = mobileKeyboardLineValue.slice(0, -1);
  };

  const noteMobileKeyboardSubmit = (): void => {
    mobileKeyboardCarryText = mobileKeyboardLineValue;
    mobileKeyboardLineValue = "";
  };

  const trimMobileKeyboardCarryText = (rawValue: string): string => {
    if (mobileKeyboardCarryText === "" || rawValue === "") {
      return rawValue;
    }

    const maxLength = Math.min(mobileKeyboardCarryText.length, rawValue.length);
    for (let length = maxLength; length > 0; length -= 1) {
      const suffix = mobileKeyboardCarryText.slice(-length);
      if (!rawValue.startsWith(suffix)) {
        continue;
      }
      if (length === rawValue.length) {
        continue;
      }
      return rawValue.slice(length);
    }
    return rawValue;
  };

  const deriveMobileKeyboardDelta = (): string => {
    const logicalValue = trimMobileKeyboardCarryText(mobileKeyboardProxy.value);
    if (logicalValue.startsWith(mobileKeyboardLineValue)) {
      return logicalValue.slice(mobileKeyboardLineValue.length);
    }
    return logicalValue;
  };

  const clearTrackedScreenKeys = (): void => {
    if (escapeReleaseTimer !== null) {
      window.clearTimeout(escapeReleaseTimer);
      escapeReleaseTimer = null;
    }
    activeScreenCodes.clear();
    activeScreenKeyCounts.clear();
  };

  const applyScreenKeyTransition = (code: string, pressed: boolean): boolean => {
    const key = KEYBOARD_CODE_TO_AM8_KEY[code];
    if (key === undefined) {
      return false;
    }

    if (pressed) {
      if (code === "Escape" && escapeReleaseTimer !== null) {
        window.clearTimeout(escapeReleaseTimer);
        escapeReleaseTimer = null;
      }
      if (activeScreenCodes.has(code)) {
        return true;
      }

      activeScreenCodes.set(code, key);
      const count = activeScreenKeyCounts.get(key) ?? 0;
      activeScreenKeyCounts.set(key, count + 1);
      if (count > 0) {
        return true;
      }
      if (!emulator.keyEvent(key, true)) {
        activeScreenCodes.delete(code);
        activeScreenKeyCounts.delete(key);
        return false;
      }
      return true;
    }

    const activeKey = activeScreenCodes.get(code);
    if (activeKey === undefined) {
      return false;
    }

    activeScreenCodes.delete(code);
    const count = activeScreenKeyCounts.get(activeKey) ?? 0;
    if (count <= 1) {
      activeScreenKeyCounts.delete(activeKey);
      return emulator.keyEvent(activeKey, false);
    }

    activeScreenKeyCounts.set(activeKey, count - 1);
    return true;
  };

  const releaseAllScreenKeys = (): void => {
    if (escapeReleaseTimer !== null) {
      window.clearTimeout(escapeReleaseTimer);
      escapeReleaseTimer = null;
    }
    for (const code of Array.from(activeScreenCodes.keys())) {
      void applyScreenKeyTransition(code, false);
    }
  };

  const pushQueuedInputByte = (value: number): boolean => {
    if (!emulator.pushInputByte(value & 0xff)) {
      setActivityStatus("Input queue full.");
      return false;
    }
    acceptedInputCount++;
    return true;
  };

  const queueMobileTextInput = (text: string): void => {
    for (const character of text) {
      let value = character.charCodeAt(0);
      if (value === 0x0a) {
        value = 0x0d;
      }
      if (value >= 0x61 && value <= 0x7a) {
        value -= 0x20;
      }
      if (value > 0xff) {
        continue;
      }
      if (!pushQueuedInputByte(value)) {
        break;
      }
    }
    mobileKeyboardProxy.value = "";
  };

  const queueHostTextInput = (text: string): void => {
    for (const character of text) {
      let value = character.charCodeAt(0);
      if (value === 0x0a) {
        value = 0x0d;
      }
      if (value >= 0x61 && value <= 0x7a) {
        value -= 0x20;
      }
      if (value > 0xff) {
        continue;
      }
      if (!pushQueuedInputByte(value)) {
        break;
      }
    }
  };

  const runImmediateScreenKeyBurst = (code: string): void => {
    if (
      SCREEN_KEYDOWN_NO_BURST.has(code) ||
      !runloop.isRunning() ||
      runloop.isDebuggerActive() ||
      emulator.isHalted()
    ) {
      return;
    }
    const beforeCycles = emulator.getCycleCount();
    const beforeFrames = emulator.getFrameNo();
    emulator.runCycles(code === "Escape" ? SCREEN_KEYDOWN_CYCLE_BURST * 100 : SCREEN_KEYDOWN_CYCLE_BURST);
    noteIgnoredRuntimeBurst(beforeCycles, beforeFrames, emulator.getCycleCount(), emulator.getFrameNo());
  };

  const scheduleScreenKeyRelease = (code: string): boolean => {
    if (code === "Escape") {
      if (!activeScreenCodes.has(code)) {
        return false;
      }
      if (escapeReleaseTimer !== null) {
        window.clearTimeout(escapeReleaseTimer);
      }
      escapeReleaseTimer = window.setTimeout(() => {
        escapeReleaseTimer = null;
        void applyScreenKeyTransition(code, false);
      }, 100);
      return true;
    }
    return applyScreenKeyTransition(code, false);
  };

  const renderDiskButtons = (): void => {
    for (const disk of diskStates) {
      disk.button.disabled = disk.busy;
      for (const bundledButton of disk.bundledButtons) {
        bundledButton.disabled = disk.busy;
      }
      disk.button.textContent = disk.mounted ? `Eject Disk ${disk.unit}` : `Insert Disk ${disk.unit}`;
      disk.button.classList.toggle("is-dirty", disk.mounted && disk.dirty);
    }
  };

  const setBundledDiskMenuOpen = (menu: BundledDiskMenuState, open: boolean): void => {
    menu.panel.hidden = !open;
    menu.trigger.setAttribute("aria-expanded", open ? "true" : "false");
  };

  const closeBundledDiskMenus = (): void => {
    for (const menu of bundledDiskMenus) {
      setBundledDiskMenuOpen(menu, false);
    }
  };

  const toggleBundledDiskMenu = (menu: BundledDiskMenuState): void => {
    const nextOpen = menu.panel.hidden;
    closeBundledDiskMenus();
    setBundledDiskMenuOpen(menu, nextOpen);
  };

  const renderDiskStatus = (): void => {
    diskStatus.textContent = diskStates
      .map((disk) =>
        disk.mounted ? `${diskLabel(disk.unit)}: ${disk.fileName ?? "mounted"}` : `${diskLabel(disk.unit)}: empty`
      )
      .join(" | ");
  };

  const reportDiskError = (error: unknown): void => {
    const message = error instanceof Error ? error.message : String(error);
    lastError = message;
    setActivityStatus(message);
    console.error(error);
  };

  const tryStartAudio = (): Promise<void> => {
    return audioBridge
      .start()
      .then(() => {
        renderAudioDebug();
      })
      .catch((error) => {
        renderAudioDebug();
        reportDiskError(error);
      });
  };

  const disableDiskPersistence = (error: unknown): void => {
    if (!diskPersistenceEnabled) {
      return;
    }

    diskPersistenceEnabled = false;
    const message = error instanceof Error ? error.message : String(error);
    setActivityStatus(`Browser disk persistence unavailable: ${message}`);
    console.error(error);
  };

  const persistDiskSnapshot = async (disk: DiskUiState, snapshot: Uint8Array): Promise<void> => {
    if (!diskPersistenceEnabled) {
      return;
    }

    try {
      await savePersistedDisk({
        fileName: disk.fileName ?? normalizeDiskDownloadName(disk.unit, null),
        image: snapshot,
        unit: disk.unit
      });
      disk.persistedImage = cloneBytes(snapshot);
      disk.persistedWriteCounter = emulator.getStorageWriteCounter(disk.unit);
    } catch (error) {
      disableDiskPersistence(error);
    }
  };

  const syncMountedDiskState = async (disk: DiskUiState, persistSnapshot = true): Promise<void> => {
    if (!disk.mounted || disk.busy) {
      return;
    }

    if (disk.dirtyOverride !== null) {
      disk.dirty = disk.dirtyOverride;
      return;
    }

    const dirty = emulator.isStorageDirty(disk.unit);
    const writeCounter = emulator.getStorageWriteCounter(disk.unit);
    disk.dirty = dirty;

    if (!diskPersistenceEnabled || !persistSnapshot) {
      return;
    }
    if (!dirty && writeCounter === disk.persistedWriteCounter) {
      return;
    }

    const snapshot = emulator.copyDiskImage(disk.unit);
    if (!bytesEqual(snapshot, disk.persistedImage)) {
      await persistDiskSnapshot(disk, snapshot);
      return;
    }
    disk.persistedWriteCounter = writeCounter;
  };

  const finishDiskMount = async (
    disk: DiskUiState,
    image: Uint8Array,
    fileName: string
  ): Promise<void> => {
    if (image.length !== emulator.getDiskImageSize()) {
      throw new Error(
        `${fileName} is ${image.length} bytes, expected ${emulator.getDiskImageSize()} bytes for .am8dsk`
      );
    }

    emulator.mountDiskImage(disk.unit, image);
    disk.mounted = true;
    disk.fileName = fileName;
    disk.dirty = false;
    disk.dirtyOverride = null;
    disk.persistedImage = cloneBytes(image);
    disk.persistedWriteCounter = emulator.getStorageWriteCounter(disk.unit);
    await persistDiskSnapshot(disk, image);
    lastError = null;
    setActivityStatus(`Mounted ${fileName} in ${diskLabel(disk.unit)}.`);
    renderDiskStatus();
    focusScreen();
    tryStartAudio();
  };

  renderAudioDebug();

  const fetchBundledDiskImage = async (url: string, fileName: string): Promise<Uint8Array> => {
    const cached = bundledDiskCache.get(url);
    if (cached !== undefined) {
      return cloneBytes(cached);
    }

    const response = await fetch(url);
    if (!response.ok) {
      const detail = response.statusText.trim();
      throw new Error(
        detail === ""
          ? `failed to load bundled disk ${fileName} (${response.status})`
          : `failed to load bundled disk ${fileName}: ${response.status} ${detail}`
      );
    }

    const image = new Uint8Array(await response.arrayBuffer());
    bundledDiskCache.set(url, cloneBytes(image));
    return image;
  };

  const deletePersistedDiskSnapshot = async (unit: StorageUnit): Promise<void> => {
    if (!diskPersistenceEnabled) {
      return;
    }

    try {
      await deletePersistedDisk(unit);
    } catch (error) {
      disableDiskPersistence(error);
    }
  };

  const refreshMountedDisks = async (): Promise<void> => {
    if (diskRefreshInFlight) {
      return;
    }

    diskRefreshInFlight = true;
    try {
      for (const disk of diskStates) {
        await syncMountedDiskState(disk, true);
      }
    } catch (error) {
      reportDiskError(error);
    } finally {
      diskRefreshInFlight = false;
      renderDiskButtons();
    }
  };

  const restorePersistedDisks = async (): Promise<void> => {
    for (const disk of diskStates) {
      if (!diskPersistenceEnabled) {
        return;
      }

      let persisted = null;
      try {
        persisted = await loadPersistedDisk(disk.unit);
      } catch (error) {
        disableDiskPersistence(error);
        return;
      }

      if (persisted === null) {
        continue;
      }

      if (persisted.image.byteLength !== emulator.getDiskImageSize()) {
        await deletePersistedDiskSnapshot(disk.unit);
        continue;
      }

      emulator.mountDiskImage(disk.unit, persisted.image);
      disk.mounted = true;
      disk.fileName = persisted.fileName;
      disk.dirty = false;
      disk.dirtyOverride = null;
      disk.persistedImage = cloneBytes(persisted.image);
      disk.persistedWriteCounter = emulator.getStorageWriteCounter(disk.unit);
    }
  };

  const promptDiskEject = (disk: DiskUiState): Promise<DiskEjectChoice> => {
    diskEjectMessage.textContent = `${disk.fileName ?? normalizeDiskDownloadName(disk.unit, null)} changed in ${diskLabel(disk.unit)}. Choose whether to download it before ejecting.`;
    if (diskEjectDialog.open) {
      diskEjectDialog.close("cancel");
    }

    return new Promise((resolve) => {
      const handleClose = (): void => {
        const choice = diskEjectDialog.returnValue;
        resolve(choice === "download" || choice === "eject" ? choice : "cancel");
      };

      diskEjectDialog.addEventListener("close", handleClose, { once: true });
      diskEjectDialog.showModal();
    });
  };

  const prepareDiskForMount = async (disk: DiskUiState): Promise<boolean> => {
    if (disk.mounted) {
      await syncMountedDiskState(disk, false);
      renderDiskButtons();
      if (!disk.dirty) {
        await ejectDisk(disk, "eject");
      } else {
        const choice = await promptDiskEject(disk);
        await ejectDisk(disk, choice);
      }
    }

    return !disk.mounted;
  };

  const mountSelectedDisk = async (disk: DiskUiState): Promise<void> => {
    const file = disk.fileInput.files?.item(0);
    disk.fileInput.value = "";
    if (file === null || file === undefined) {
      return;
    }

    disk.busy = true;
    renderDiskButtons();
    try {
      const image = new Uint8Array(await file.arrayBuffer());
      await finishDiskMount(disk, image, file.name);
    } catch (error) {
      reportDiskError(error);
    } finally {
      disk.busy = false;
      renderDiskButtons();
    }
  };

  const ejectDisk = async (disk: DiskUiState, choice: DiskEjectChoice): Promise<void> => {
    if (choice === "cancel") {
      setActivityStatus(`${diskLabel(disk.unit)} stays inserted.`);
      focusScreen();
      return;
    }

    disk.busy = true;
    renderDiskButtons();
    try {
      const wasDirty = disk.dirty;
      emulator.syncStorage(disk.unit);

      let snapshot: Uint8Array | null = null;
      if (choice === "download") {
        snapshot = emulator.copyDiskImage(disk.unit);
      }

      emulator.ejectStorage(disk.unit);

      if (snapshot !== null) {
        triggerDiskDownload(snapshot, normalizeDiskDownloadName(disk.unit, disk.fileName));
      }

      const previousFileName = disk.fileName;
      disk.mounted = false;
      disk.dirty = false;
      disk.fileName = null;
      disk.dirtyOverride = null;
      disk.persistedImage = null;
      disk.persistedWriteCounter = 0;
      await deletePersistedDiskSnapshot(disk.unit);
      lastError = null;
      setActivityStatus(
        choice === "download"
          ? `Downloaded and ejected ${previousFileName ?? diskLabel(disk.unit)}.`
          : wasDirty
            ? `Ejected ${previousFileName ?? diskLabel(disk.unit)} without download.`
            : `Ejected ${previousFileName ?? diskLabel(disk.unit)}.`
      );
      renderDiskStatus();
      focusScreen();
    } catch (error) {
      reportDiskError(error);
    } finally {
      disk.busy = false;
      renderDiskButtons();
    }
  };

  diskEjectDialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    diskEjectDialog.close("cancel");
  });

  for (const menu of bundledDiskMenus) {
    menu.trigger.addEventListener("click", () => {
      toggleBundledDiskMenu(menu);
    });
  }

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }

    for (const menu of bundledDiskMenus) {
      if (menu.trigger.contains(target) || menu.panel.contains(target)) {
        return;
      }
    }

    closeBundledDiskMenus();
  });

  diskLibrary.addEventListener("toggle", () => {
    if (!diskLibrary.open) {
      closeBundledDiskMenus();
    }
  });

  for (const disk of diskStates) {
    disk.button.addEventListener("click", async () => {
      if (disk.busy) {
        return;
      }

      if (!disk.mounted) {
        disk.fileInput.value = "";
        disk.fileInput.click();
        return;
      }

      await syncMountedDiskState(disk, false);
      renderDiskButtons();

      if (!disk.dirty) {
        await ejectDisk(disk, "eject");
        return;
      }

      const choice = await promptDiskEject(disk);
      await ejectDisk(disk, choice);
    });

    disk.fileInput.addEventListener("change", async () => {
      await mountSelectedDisk(disk);
    });
  }

  for (const control of bundledDiskControls) {
    control.button.addEventListener("click", async () => {
      closeBundledDiskMenus();
      if (control.disk.busy) {
        return;
      }

      if (!(await prepareDiskForMount(control.disk))) {
        return;
      }

      control.disk.busy = true;
      renderDiskButtons();
      try {
        const image = await fetchBundledDiskImage(control.url, control.fileName);
        await finishDiskMount(control.disk, image, control.fileName);
      } catch (error) {
        reportDiskError(error);
      } finally {
        control.disk.busy = false;
        renderDiskButtons();
      }
    });
  }

  for (const rom of DEFAULT_ROMS) {
    setActivityStatus(`Loading ${rom.label}...`);
    await emulator.loadRomFromUrl(rom.kind, rom.url);
  }

  emulator.reset();
  await restorePersistedDisks();

  const syncEmulatorStepControls = (): void => {
    const halted = emulator.isHalted();
    const canStep = !runloop.isRunning() && !halted;
    emulatorStep.disabled = !canStep;
    emulatorStep10.disabled = !canStep;
  };

  const runloop = new Runloop(emulator, renderer, {
    onAfterRun() {
      audioBridge.pump();
    },
    onMetrics(snapshot) {
      renderStats(snapshot);
      workbench.updateDebuggerRuntime(snapshot);
      workbench.syncTracker();
      syncEmulatorStepControls();
    },
    onDebuggerStop() {
      workbench.refreshDebugger();
      syncEmulatorStepControls();
    },
    onRunningChange(running) {
      toggleRun.textContent = running ? "Pause" : "Resume";
      syncEmulatorStepControls();
    },
    onStatus(message) {
      setActivityStatus(message);
    }
  });

  const currentMetrics = (): DesktopWebMetrics => ({
    cycleAccumulator: runloop.getCycleAccumulator(),
    cycleCount: emulator.getCycleCount().toString(),
    debuggerActive: runloop.isDebuggerActive(),
    frameBacklog: runloop.getFrameBacklog(),
    frameNo: emulator.getFrameNo(),
    halted: emulator.isHalted(),
    presentCount: renderer.getPresentCount(),
    presentedFrameNo: runloop.getPresentedFrameNo(),
    running: runloop.isRunning(),
    skippedFrameCount: runloop.getSkippedFrameCount(),
    state: runloop.getState(),
    stopReason: runloop.getStopReason()
  });

  const pauseExecution = (): void => {
    runloop.pauseDebugger();
  };

  const resumeExecution = (focusAfterResume = false): void => {
    runloop.continueDebugger();
    tryStartAudio();
    renderStats(currentMetrics());
    if (focusAfterResume) {
      focusScreen();
    }
  };

  workbench.setDebuggerActions({
    activate() {
      runloop.activateDebugger();
    },
    continue() {
      resumeExecution();
    },
    pause() {
      pauseExecution();
    },
    step(count) {
      runloop.stepDebugger(count);
    }
  });
  workbench.updateDebuggerRuntime({
    cycleCount: emulator.getCycleCount().toString(),
    debuggerActive: runloop.isDebuggerActive(),
    frameNo: emulator.getFrameNo(),
    state: runloop.getState(),
    stopReason: runloop.getStopReason()
  });
  syncEmulatorStepControls();

  window.__am8DesktopWeb = {
    copyMemory(address, length) {
      return Array.from(emulator.copyMemory(address, length));
    },
    focusScreen() {
      focusScreen();
    },
    getSnapshot() {
      const rendererDebug = renderer.getDebugState();
      return {
        booted: true,
        cycleAccumulator: runloop.getCycleAccumulator(),
        cycleCount: emulator.getCycleCount().toString(),
        debuggerActive: runloop.isDebuggerActive(),
        frameBacklog: runloop.getFrameBacklog(),
        frameNo: emulator.getFrameNo(),
        halted: emulator.isHalted(),
        inputCount: acceptedInputCount,
        lastError,
        presentCount: renderer.getPresentCount(),
        presentedFrameNo: runloop.getPresentedFrameNo(),
        rendererBackend: rendererDebug.backend,
        rendererFallbackReason: rendererDebug.fallbackReason,
        runState: runloop.getState(),
        running: runloop.isRunning(),
        screenFocused: document.activeElement === canvas || document.activeElement === mobileKeyboardProxy,
        skippedFrameCount: runloop.getSkippedFrameCount(),
        stopReason: runloop.getStopReason(),
        statusText: activityStatus.textContent ?? ""
      };
    },
    pause() {
      pauseExecution();
    },
    queueInput(text) {
      queueHostTextInput(text);
    },
    reset() {
      clearTrackedScreenKeys();
      emulator.reset();
      workbench.refreshDebugger();
      workbench.refreshCharacters();
      workbench.refreshSprites();
      renderStats(currentMetrics());
    },
    resume() {
      resumeExecution();
    },
    writeMemory(address, data) {
      emulator.writeMemory(address, new Uint8Array(data));
    },
    setDiskDirtyForTest(unit, dirty) {
      const disk = diskStates.find((entry) => entry.unit === unit);
      if (disk === undefined || !disk.mounted) {
        return;
      }

      disk.dirtyOverride = dirty ? true : null;
      disk.dirty = dirty ? true : emulator.isStorageDirty(unit);
      renderDiskButtons();
    }
  };

  toggleRun.addEventListener("click", () => {
    if (runloop.isRunning()) {
      pauseExecution();
    } else {
      resumeExecution(true);
    }
  });

  emulatorStep.addEventListener("click", () => {
    runloop.stepDebugger(1);
    syncEmulatorStepControls();
  });

  emulatorStep10.addEventListener("click", () => {
    runloop.stepDebugger(10);
    syncEmulatorStepControls();
  });

  reset.addEventListener("click", () => {
    clearTrackedScreenKeys();
    emulator.reset();
    setActivityStatus("Emulator reset.");
    if (!runloop.isRunning()) {
      runloop.start();
    }
    tryStartAudio();
    workbench.refreshDebugger();
    workbench.refreshCharacters();
    workbench.refreshSprites();
    renderStats(currentMetrics());
    syncEmulatorStepControls();
    focusScreen();
  });

  mobileKeyboardOpen.addEventListener("click", () => {
    tryStartAudio();
    focusMobileKeyboard();
  });

  mobileKeyboardProxy.addEventListener("keydown", (event) => {
    captureMobileKeyboardScroll();
    if (event.key === "Backspace") {
      mobileKeyboardPendingSpecialInput = "backspace";
      void pushQueuedInputByte(0x08);
      noteMobileKeyboardBackspace();
      mobileKeyboardProxy.value = "";
      event.preventDefault();
      scheduleMobileKeyboardScrollRestore();
      return;
    }
    if (event.key === "Enter") {
      mobileKeyboardPendingSpecialInput = "enter";
      void pushQueuedInputByte(0x0d);
      noteMobileKeyboardSubmit();
      mobileKeyboardProxy.value = "";
      event.preventDefault();
      scheduleMobileKeyboardScrollRestore();
    }
  });

  mobileKeyboardProxy.addEventListener("beforeinput", (event) => {
    captureMobileKeyboardScroll();
    if (event.inputType === "deleteContentBackward") {
      if (mobileKeyboardPendingSpecialInput === "backspace") {
        mobileKeyboardPendingSpecialInput = null;
        mobileKeyboardProxy.value = "";
        event.preventDefault();
        return;
      }
      void pushQueuedInputByte(0x08);
      noteMobileKeyboardBackspace();
      mobileKeyboardProxy.value = "";
      event.preventDefault();
      return;
    }
    if (event.inputType === "insertLineBreak" || event.inputType === "insertParagraph") {
      if (mobileKeyboardPendingSpecialInput === "enter") {
        mobileKeyboardPendingSpecialInput = null;
        mobileKeyboardProxy.value = "";
        event.preventDefault();
        return;
      }
      void pushQueuedInputByte(0x0d);
      noteMobileKeyboardSubmit();
      mobileKeyboardProxy.value = "";
      event.preventDefault();
      return;
    }
  });

  mobileKeyboardProxy.addEventListener("input", (event) => {
    if (event instanceof InputEvent) {
      if (event.inputType === "deleteContentBackward") {
        if (mobileKeyboardPendingSpecialInput === "backspace") {
          mobileKeyboardPendingSpecialInput = null;
          mobileKeyboardProxy.value = "";
          return;
        }
        void pushQueuedInputByte(0x08);
        noteMobileKeyboardBackspace();
        mobileKeyboardProxy.value = "";
        scheduleMobileKeyboardScrollRestore();
        return;
      }
      if (event.inputType === "insertLineBreak" || event.inputType === "insertParagraph") {
        if (mobileKeyboardPendingSpecialInput === "enter") {
          mobileKeyboardPendingSpecialInput = null;
          mobileKeyboardProxy.value = "";
          return;
        }
        void pushQueuedInputByte(0x0d);
        noteMobileKeyboardSubmit();
        mobileKeyboardProxy.value = "";
        scheduleMobileKeyboardScrollRestore();
        return;
      }
      if (event.inputType === "insertText" && typeof event.data === "string" && event.data !== "") {
        queueMobileTextInput(event.data);
        noteMobileKeyboardText(event.data);
        scheduleMobileKeyboardScrollRestore();
        return;
      }
    }
    mobileKeyboardPendingSpecialInput = null;
    if (mobileKeyboardProxy.value === "") {
      scheduleMobileKeyboardScrollRestore();
      return;
    }

    const fallbackText = deriveMobileKeyboardDelta();
    if (fallbackText === "") {
      mobileKeyboardProxy.value = "";
      return;
    }
    queueMobileTextInput(fallbackText);
    noteMobileKeyboardText(fallbackText);
    scheduleMobileKeyboardScrollRestore();
  });

  canvas.addEventListener("keydown", (event) => {
    if (event.code === "F9") {
      reset.click();
      event.preventDefault();
      return;
    }

    if (!applyScreenKeyTransition(event.code, true)) {
      return;
    }

    acceptedInputCount++;
    runImmediateScreenKeyBurst(event.code);
    if (event.code === "Escape") {
      setActivityStatus("Stop requested.");
      renderStats(currentMetrics());
    }

    event.preventDefault();
  });

  canvas.addEventListener("keyup", (event) => {
    if (event.code === "F9") {
      event.preventDefault();
      return;
    }
    if (!scheduleScreenKeyRelease(event.code)) {
      return;
    }
    event.preventDefault();
  });

  screenFrame.addEventListener("pointerdown", () => {
    releaseAllScreenKeys();
    tryStartAudio();
    focusScreen();
  });

  canvas.addEventListener("focus", () => {
    renderScreenFocus(true, "screen");
  });

  canvas.addEventListener("blur", () => {
    releaseAllScreenKeys();
    if (document.activeElement !== mobileKeyboardProxy) {
      renderScreenFocus(false);
    }
  });

  mobileKeyboardProxy.addEventListener("focus", () => {
    renderScreenFocus(true, "mobile");
  });

  mobileKeyboardProxy.addEventListener("blur", () => {
    if (mobileKeyboardScrollRestoreFrame !== 0) {
      window.cancelAnimationFrame(mobileKeyboardScrollRestoreFrame);
      mobileKeyboardScrollRestoreFrame = 0;
    }
    mobileKeyboardProxy.value = "";
    mobileKeyboardCarryText = "";
    mobileKeyboardLineValue = "";
    mobileKeyboardPendingSpecialInput = null;
    if (document.activeElement !== canvas) {
      renderScreenFocus(false);
    }
  });

  renderDiskButtons();
  renderDiskStatus();
  renderScreenFocus(false);
  window.setInterval(() => {
    void refreshMountedDisks();
  }, DISK_PERSIST_INTERVAL_MS);
  focusScreen();
  renderer.present(emulator.getFramebufferView());
  runloop.markFramePresented();
  renderStats(currentMetrics());
  runloop.start();
  if ((activityStatus.textContent ?? "").startsWith("Loading ")) {
    setActivityStatus("Browser host ready.");
  }
  renderStats(currentMetrics());
}

bootstrap().catch((error) => {
  const message = error instanceof Error ? error.message : String(error);
  const status = document.getElementById("status");
  const activityStatus = document.getElementById("activity-status");
  if (status !== null) {
    status.textContent = "halted";
    status.setAttribute("data-state", "halted");
  }
  if (activityStatus !== null) {
    activityStatus.textContent = "Desktop-web bootstrap failed.";
  }
  window.__am8DesktopWeb = {
    copyMemory() {
      return [];
    },
    getSnapshot() {
      return {
        booted: false,
        cycleAccumulator: 0,
        cycleCount: "0",
        frameBacklog: 0,
        frameNo: 0,
        halted: false,
        inputCount: 0,
        lastError: message,
        presentCount: 0,
        presentedFrameNo: 0,
        rendererBackend: null,
        rendererFallbackReason: null,
        running: false,
        screenFocused: false,
        skippedFrameCount: 0,
        statusText: activityStatus?.textContent ?? ""
      };
    },
    focusScreen() {},
    pause() {},
    queueInput() {},
    reset() {},
    resume() {},
    writeMemory() {},
    setDiskDirtyForTest() {}
  };
  console.error(error);
});
